// Primary domain. All canonical, Open Graph and structured-data URLs resolve against this
// so that the emergent.host and www variants never compete for indexing.
export const SITE_URL = "https://nandanaayaspices.com";

export const BUSINESS = {
  name: "Nandanaaya Spices",  nameCaps: "NANDANAAYA SPICES",
  tagline: "FOR A HEALTHIER TOMORROW",
  proprietor: "G. Chandrashekhar",
  logo: "/img/logo.webp",
  // Owner-verified certificate values (confirmed 24 June 2026). Do not alter.
  gstin: "36AOLPC5543B1ZA",
  fssai: "23626125000049",
  udyam: "UDYAM-TS-02-0370588",
  phoneDisplay: "+91 9951661535",
  phoneRaw: "+919951661535",
  whatsappNumber: "919951661535",
  email: "Nandanaayaspices999@gmail.com",
  address: "Saidabad, Hyderabad, Telangana – 500059",
  locality: "Saidabad, Hyderabad",
  region: "Telangana",
  postalCode: "500059",
  city: "Hyderabad",
  hours: "9:00 AM – 6:00 PM",
  // Legal drafts are not yet owner/legal approved. Set legalUpdated to the actual
  // owner-approval / publication date and flip legalReviewPending to false once approved.
  legalReviewPending: false,
  legalUpdated: "24 June 2026",
};

const WHATSAPP_PREFILL = (product = "") => `Hello Nandanaaya Spices, I would like a current quotation.

Name:
Business name:
City:
Product: ${product}
Grade/specification:
Quantity:
Purchase timeline:
GST invoice required: Yes/No`;

export const whatsappUrl = (product = "") =>
  `https://wa.me/${BUSINESS.whatsappNumber}?text=${encodeURIComponent(WHATSAPP_PREFILL(product))}`;

export const WHATSAPP_URL = whatsappUrl();

export const SAMPLE_WHATSAPP_URL = `https://wa.me/${BUSINESS.whatsappNumber}?text=${encodeURIComponent(
  `Hello Nandanaaya Spices, I would like to request an actual-lot sample.

Name:
Business name:
City:
Product:
Grade/specification:
Quantity likely to order:
Sample dispatch address:`
)}`;

export const CALL_URL = `tel:${BUSINESS.phoneRaw}`;
export const EMAIL_URL = `mailto:${BUSINESS.email}`;

// Every website photograph is illustrative unless the owner confirms it as current stock.
export const PHOTO_LABEL = "Representative photograph";

export const IMAGES = {
  hero: {
    url: "/img/hero.webp",
    alt: "Assorted whole spices in bowls, representative photograph",
    width: 1200,
    height: 799,
  },
  social: "/img/whole-spices.webp",
  gallery: [
    {
      url: "/img/cardamom-bold.webp",
      alt: "Bold green cardamom pods held in hand",
      label: "Bold pods",
      width: 960,
      height: 1280,
    },
    {
      url: "/img/cardamom-sack.webp",
      alt: "Green cardamom lot packed in a jute sack",
      label: "Packed lot",
      width: 980,
      height: 1600,
    },
    {
      url: "/img/cardamom-lot.webp",
      alt: "Jute sacks filled with graded green cardamom ready for dispatch",
      label: "Grade-wise sacks",
      width: 1200,
      height: 729,
    },
    {
      url: "/img/cardamom-pod.webp",
      alt: "Green cardamom pod opened to show the seeds inside",
      label: "Pod inspection",
      width: 1200,
      height: 743,
    },
  ],
};

export const PRODUCTS = [
  {
    id: "green-cardamom",
    name: "Green Cardamom",
    tag: "Core Specialisation",
    tagClass: "bg-spice-green",
    description: "Green cardamom supplied grade-wise by indicative pod size, colour tone and lot appearance.",
    applications: "Sweet shops, tea blending, retail packing, bakeries, institutional catering.",
    image: {
      url: "/img/green-cardamom.webp",
      alt: "Green cardamom pods in a jute bag, representative photograph",
      width: 967,
      height: 1300,
    },
  },
  {
    id: "black-pepper",
    name: "Black Pepper",
    tag: "Selected Lots",
    tagClass: "bg-spice-charcoal",
    description: "Whole dried black peppercorns selected according to visible lot characteristics and berry size.",
    applications: "Masala blenders, commercial caterers, food processing, hotel supply.",
    image: {
      url: "/img/black-pepper.webp",
      alt: "Whole dried black peppercorns, representative photograph",
      width: 1200,
      height: 778,
    },
  },
  {
    id: "nutmeg",
    name: "Nutmeg (Jaiphal)",
    tag: "Whole Seed",
    tagClass: "bg-spice-nutmeg",
    description: "Whole nutmeg selected for sound, unbroken seed condition and natural aroma on inspection.",
    applications: "Confectioneries, spice powders, bakeries, ayurvedic and retail packing.",
    image: {
      url: "/img/nutmeg.webp",
      alt: "Whole shelled nutmeg seeds, representative photograph",
      width: 1200,
      height: 675,
    },
  },
  {
    id: "mace",
    name: "Mace (Javitri)",
    tag: "Golden Mace",
    tagClass: "bg-spice-mace",
    description: "Dried mace blades with natural saffron-orange tone, supplied against the available lot.",
    applications: "Biryani catering, gourmet spice blends, Mughlai cuisine, retail packs.",
    image: {
      url: "/img/mace.webp",
      alt: "Dried mace blades in a wooden bowl, representative photograph",
      width: 1200,
      height: 1026,
    },
  },
  {
    id: "cloves",
    name: "Cloves (Laung)",
    tag: "Intact Heads",
    tagClass: "bg-spice-clove",
    description: "Whole cloves selected for intact crowns and uniform colour at the time of lot inspection.",
    applications: "Garam masala production, tea blenders, restaurants, retail packing.",
    image: {
      url: "/img/cloves.webp",
      alt: "Whole cloves with intact crown heads, representative photograph",
      width: 1200,
      height: 799,
    },
  },
  {
    id: "bay-leaves",
    name: "Bay Leaves (Tejpatta)",
    tag: "Dried Whole Leaf",
    tagClass: "bg-spice-bay",
    description: "Dried whole leaves selected for unbroken condition and carefully handled during packing.",
    applications: "Commercial catering, rice preparation, grocery stores, institutional kitchens.",
    image: {
      url: "/img/bay-leaves.webp",
      alt: "Dried whole tejpatta bay leaves, representative photograph",
      width: 1200,
      height: 848,
    },
  },
  {
    id: "dry-coconut",
    name: "Dry Coconut (Copra / Khopra)",
    tag: "Clean Copra",
    tagClass: "bg-spice-copra",
    description: "Dried copra supplied against the available lot, checked visually for spoilage before packing.",
    applications: "Sweet manufacturers, confectionery units, dry-fruit retailers, wholesale supply.",
    image: {
      url: "/img/dry-coconut.webp",
      alt: "Dry coconut copra halves, representative photograph",
      width: 1200,
      height: 675,
    },
  },
  {
    id: "other-whole-spices",
    name: "Other Whole Spices",
    tag: "On Commercial Enquiry",
    tagClass: "bg-spice-green",
    description: "Star anise, cinnamon, cumin, fennel and other whole spices arranged on commercial enquiry.",
    applications: "Custom wholesale consignments, seasonal blending lots, bulk distribution.",
    image: {
      url: "/img/whole-spices.webp",
      alt: "Assorted whole spices in bowls, representative photograph",
      width: 1200,
      height: 800,
    },
  },
];

export const CARDAMOM_GRADES = [
  {
    size: "6–7 mm",
    category: "Commercial / Grinding",
    buyers: "Masala formulation, powdering, catering batches, budget retail packing.",
  },
  {
    size: "7–7.5 mm",
    category: "Regular Retail",
    buyers: "Kirana stores, regional supermarkets, sweet shops.",
  },
  {
    size: "7.5–8 mm",
    category: "Selected Medium-Bold",
    buyers: "Dry-fruit boutiques, gift packing, premium retail brands.",
  },
  {
    size: "8 mm+",
    category: "Bold / Extra-Bold",
    buyers: "Buyers who prefer larger pod appearance for display and gift packing.",
  },
];

export const PRODUCT_OPTIONS = [
  "Green Cardamom",
  "Black Pepper",
  "Nutmeg (Jaiphal)",
  "Mace (Javitri)",
  "Cloves (Laung)",
  "Bay Leaves (Tejpatta)",
  "Dry Coconut (Copra / Khopra)",
  "Other Whole Spices",
  "Multiple Products",
];

export const NAV_LINKS = [
  { label: "Home", to: "/", testid: "nav-home" },
  { label: "Products", to: "/products", testid: "nav-products" },
  { label: "Cardamom Grades", to: "/products#cardamom-grades", testid: "nav-cardamom-grades" },
  { label: "About Us", to: "/about", testid: "nav-about" },
  { label: "Quality & Process", to: "/quality-and-process", testid: "nav-quality-process" },
  { label: "Contact", to: "/contact", testid: "nav-contact" },
];

export const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Shipping & Delivery Policy", to: "/shipping-and-delivery" },
  { label: "Cancellation & Refund Policy", to: "/cancellation-and-refund" },
  { label: "Disclaimer", to: "/disclaimer" },
];
